import type { Infer } from 'spacetimedb';
import {
  DbConnection,
  MapDefinitionsRow,
  MapPortalsRow,
  CurrentMapObjectsRow,
  CurrentMapNpcsRow,
  CurrentMapPortalsRow,
  CurrentMapDefinitionRow,
  reducers
} from '../generated';
import type { ConnectionManager } from '../core/ConnectionManager';
import type { ReducerClient } from '../core/ReducerClient';
import type { SubscriptionFactory } from '../core/SubscriptionFactory';
import type {
  AuthoritativeMapDefinition,
  AuthoredBiomeMetadata,
  MapPortalRecord,
  MapObjectPlacementRecord,
  PlacedNpcRecord,
  SpawnPoint,
  MapPayload,
  MapRulesMetadata,
  Logger
} from '../types';
import { coerceAuthoritativeMapObjectRecord } from '../../maps/mapObjectPersistence';

type MapDefinitionsTableRow = Infer<typeof CurrentMapDefinitionRow>;
type MapPortalsTableRow = Infer<typeof CurrentMapPortalsRow>;
type MapObjectsTableRow = Infer<typeof CurrentMapObjectsRow>;
type CurrentMapNpcsRow = Infer<typeof CurrentMapNpcsRow>;

export class MapModule {
  private mapDefinitionCache = new Map<string, AuthoritativeMapDefinition>();
  private npcCache = new Map<string, PlacedNpcRecord[]>();

  constructor(
    private readonly connectionManager: ConnectionManager,
    private readonly reducerClient: ReducerClient,
    private readonly subscriptionFactory: SubscriptionFactory,
    private readonly logger: Logger = () => undefined
  ) { }

  public async getMapDefinition(
    mapId: string,
    options?: { forceRefresh?: boolean }
  ): Promise<AuthoritativeMapDefinition | null> {
    if (!options?.forceRefresh) {
      const cached = this.mapDefinitionCache.get(mapId);
      if (cached) {
        return cached;
      }
    }

    const connection = await this.connectionManager.ensureConnection();
    await this.authorizeMapAccess([mapId]);
    const row = await this.runMapDefinitionQuery(connection, mapId);
    if (!row) {
      return null;
    }

    const portals = await this.runMapPortalQuery(connection, mapId);
    const objects = await this.runMapObjectsQuery(connection, mapId);
    const definition = this.mapDefinitionFromRow(row, portals, objects);
    this.mapDefinitionCache.set(mapId, definition);
    return definition;
  }

  public clearMapDefinitionCache(mapId?: string): void {
    if (typeof mapId === 'string') {
      this.mapDefinitionCache.delete(mapId);
    } else {
      this.mapDefinitionCache.clear();
    }
  }

  public peekMapDefinition(mapId: string): AuthoritativeMapDefinition | null {
    return this.mapDefinitionCache.get(mapId) ?? null;
  }

  public clearNpcCache(mapId?: string): void {
    if (typeof mapId === 'string') {
      this.npcCache.delete(mapId);
    } else {
      this.npcCache.clear();
    }
  }

  public async usePortal(): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'use_portal',
      paramsType: reducers.usePortal.paramsType,
      args: {},
      onEvent: 'onUsePortal',
      offEvent: 'removeOnUsePortal',
      timeoutMessage: 'Portal transition timed out',
      failureMessage: 'Portal transition failed'
    });
  }

  public async fetchNpcsForMap(
    mapId: string,
    options?: { forceRefresh?: boolean }
  ): Promise<PlacedNpcRecord[]> {
    if (!options?.forceRefresh) {
      const cached = this.npcCache.get(mapId);
      if (cached) {
        return cached;
      }
    }

    const connection = await this.connectionManager.ensureConnection();
    await this.authorizeMapAccess([mapId]);
    const npcs = await this.runNpcsQuery(connection, mapId);
    this.npcCache.set(mapId, npcs);
    return npcs;
  }

  private async authorizeMapAccess(mapIds: string[]): Promise<void> {
    if (mapIds.length === 0) {
      return;
    }

    const playerId = this.connectionManager.requirePlayerId();
    await this.reducerClient.callAndConfirm({
      reducerName: 'verify_map_access',
      paramsType: reducers.verifyMapAccess.paramsType,
      args: { playerId, mapIds },
      onEvent: 'onVerifyMapAccess',
      offEvent: 'removeOnVerifyMapAccess',
      matchArgs: (args) => args.playerId === playerId,
      timeoutMessage: 'Map access timed out',
      failureMessage: 'Map access denied'
    });
  }

  private async runMapDefinitionQuery(
    _connection: DbConnection,
    _mapId: string
  ): Promise<MapDefinitionsTableRow | null> {
    const results = await this.subscriptionFactory.runOneTimeQuery({
      sql: 'SELECT * FROM current_map_definition',
      getTable: (db) => db.currentMapDefinition,
      mapRow: (row) => row as MapDefinitionsTableRow
    });
    return results[0] ?? null;
  }

  private async runMapPortalQuery(
    _connection: DbConnection,
    _mapId: string
  ): Promise<MapPortalRecord[]> {
    return this.subscriptionFactory.runOneTimeQuery({
      sql: 'SELECT * FROM current_map_portals',
      getTable: (db) => db.currentMapPortals,
      mapRow: (row) => this.mapPortalRow(row as unknown as MapPortalsTableRow)
    });
  }

  private async runMapObjectsQuery(
    _connection: DbConnection,
    _mapId: string
  ): Promise<MapObjectPlacementRecord[]> {
    return this.subscriptionFactory.runOneTimeQuery({
      sql: 'SELECT * FROM current_map_objects',
      getTable: (db) => db.currentMapObjects,
      mapRow: (row) => this.mapObjectRow(row as unknown as MapObjectsTableRow)
    });
  }

  private async runNpcsQuery(
    _connection: DbConnection,
    _mapId: string
  ): Promise<PlacedNpcRecord[]> {
    return this.subscriptionFactory.runOneTimeQuery({
      sql: 'SELECT * FROM current_map_npcs',
      getTable: (db) => db.currentMapNpcs,
      mapRow: (row) => this.mapNpcRow(row as unknown as CurrentMapNpcsRow)
    });
  }

  private mapNpcRow(row: CurrentMapNpcsRow): PlacedNpcRecord {
    return {
      npcId: row.npcId,
      npcType: row.npcType,
      x: row.worldX,
      y: row.worldY,
      width: Number(row.width ?? 1),
      height: Number(row.height ?? 1),
      metadata: this.parseJson<Record<string, unknown>>(row.metadataJson, {})
    };
  }

  private mapDefinitionFromRow(
    row: MapDefinitionsTableRow,
    portals: MapPortalRecord[],
    objects: MapObjectPlacementRecord[],
  ): AuthoritativeMapDefinition {
    const spawnPoints = this.parseJson<Record<string, SpawnPoint>>(row.spawnPointsJson, {});
    const rules = this.parseJson<MapRulesMetadata>(row.rulesJson, {});
    const render = this.parseJson<unknown>((row as { renderJson?: string | null }).renderJson, null);
    const biomeMetadata = this.parseJson<AuthoredBiomeMetadata | null>(
      (row as { biomeJson?: string | null }).biomeJson,
      null,
    );
    const backend = this.parseJson<unknown>((row as { backendJson?: string | null }).backendJson, null);
    const expectedTileCount = Number(row.width) * Number(row.height);
    const tilesBlob = row.tilesBlob instanceof Uint8Array ? row.tilesBlob : new Uint8Array(row.tilesBlob ?? []);
    const terrainBlob = row.terrainBlob instanceof Uint8Array
      ? row.terrainBlob
      : new Uint8Array(row.terrainBlob ?? []);

    let payload: MapPayload;
    if (rules.tileSource === 'procedural') {
      const generatorKey = rules.proceduralGenerator ?? row.mapId;
      payload = { type: 'procedural', generatorKey };
    } else {
      let buffer = new Uint8Array(tilesBlob);
      let biomes = new Uint8Array(terrainBlob);
      if (buffer.length < expectedTileCount) {
        const padded = new Uint8Array(expectedTileCount);
        padded.set(buffer.slice(0, expectedTileCount));
        buffer = padded;
      }
      if (biomes.length < expectedTileCount) {
        const padded = new Uint8Array(expectedTileCount);
        padded.set(biomes.slice(0, expectedTileCount));
        biomes = padded;
      }
      payload = { type: 'blob', tiles: buffer, biomes };
    }

    return {
      mapId: row.mapId,
      name: row.name,
      notes: typeof (backend as { notes?: unknown } | null | undefined)?.notes === 'string'
        ? (backend as { notes: string }).notes
        : null,
      mapKind: String((row as { mapKind?: unknown }).mapKind ?? 'overworld'),
      width: row.width,
      height: row.height,
      payload,
      spawnPoints,
      rules,
      objects,
      render,
      biomeMetadata,
      backend,
      portals,
      gridType: row.gridType ?? 'hex'
    };
  }

  private mapObjectRow(row: MapObjectsTableRow): MapObjectPlacementRecord {
    return coerceAuthoritativeMapObjectRecord({
      id: row.objectId,
      asset: row.assetId,
      x: Number(row.x),
      y: Number(row.y),
      offsetX: row.offsetX,
      offsetY: row.offsetY,
      sizeMultiplier: row.sizeMultiplier,
      flipHorizontal: row.flipHorizontal === true,
      flipVertical: row.flipVertical === true,
      rotationSteps: row.rotationSteps,
      layer: row.layer,
      sortOrder: row.sortOrder,
    });
  }

  private mapPortalRow(row: MapPortalsTableRow): MapPortalRecord {
    return {
      x: row.x,
      y: row.y,
      destMapId: row.destMapId,
      destX: row.destX,
      destY: row.destY,
      destMapKind: row.destMapKind,
    };
  }

  private parseJson<T>(source: string | null | undefined, fallback: T): T {
    if (!source) {
      return fallback;
    }
    try {
      return JSON.parse(source) as T;
    } catch (error) {
      this.logger('json_parse_failure', {
        error: (error as Error).message,
        sourceSample: source.slice(0, 64)
      });
      return fallback;
    }
  }
}
